import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useQueryClient } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { ICognitoMigrationPatient } from "../../../@types/admin/AdminCognitoMigration";
import {
  StyledDialogContent,
  StyledDialogOverlay,
  StyledTile,
} from "../../../components/Modal/Modal.styles";
import { TableSorted } from "../../../components/TableSorted";
import { useMigratePatientToCognito } from "../../../hooks/mutations/admin/cognitoMigration";
import {
  COGNITO_MIGRATION_PATIENTS_QUERY_KEY,
  useCognitoMigrationPatients,
} from "../../../hooks/queries/admin/cognitoMigration";
import { useHospitalWards } from "../../../hooks/queries/admin/hospitalDetails";
import { useHospitals } from "../../../hooks/queries/admin/hospitals";
import { useDebounce } from "../../../hooks/useDebounce";
import { useModal } from "../../../hooks/useModal";
import { getLongFormattedDate } from "../../../utils/dateFormatter";
import { saveBlobAs } from "../../../utils/fileDownload";
import { errorToastMessage, HttpError } from "../../../utils/HttpError";
import {
  AdminSelectField,
  EmailLink,
  ErrorMessage,
  FilterButton,
  FilterLabel,
  FiltersActions,
  FiltersContainer,
  ModalBodyText,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
  PageContainer,
  PageHeader,
  PageInfoTooltip,
  PageTitle,
  PageTitleGroup,
  SearchInput,
  SectionCaption,
  SectionHeader,
  SectionTitle,
  SectionTitleGroup,
  SelectAllCheckbox,
  SelectCell,
  SelectionCount,
  StatCard,
  StatLabel,
  StatsGrid,
  StatValue,
  StatusBadge,
  TableContainer,
  TableLoadingState,
  TablePagination,
  TableWrapper,
  WarningNotice,
} from "../shared";
import {
  ConfirmList,
  ConfirmStatCard,
  ConfirmStatHint,
  ConfirmStatLabel,
  ConfirmStats,
  ConfirmStatValue,
  DetailCell,
  ProgressFill,
  ProgressTrack,
  RunActions,
  RunDialogBody,
  TableFetchingOverlay,
  TableStage,
  WarningDialogActions,
  WarningDialogActionsGroup,
  WarningDialogBody,
  WarningDialogHeader,
  WarningDialogIcon,
  WarningDialogTitle,
} from "./AdminCognitoMigration.styles";
import { FilterField } from "./AdminPatients.styles";
import {
  buildResultsCsv,
  hasUnsavedPasswords,
  IRunItem,
  isMigratable,
  itemDetail,
  itemResultLabel,
  itemTone,
  planRun,
  resultsFilename,
  STATUS_LABEL,
  statusTone,
  summarize,
} from "./cognitoMigrationRun";
import { passwordSetTone } from "./patientListFields";

const DEFAULT_PAGE_SIZE = 25;

/** Floor between two migrate requests; the API caps the endpoint at 120/min. */
const MIN_REQUEST_INTERVAL_MS = 600;

/** How long to wait before the single retry of a rate-limited request. */
const RATE_LIMIT_RETRY_MS = 1500;

function sleep(ms: number): Promise<void> {
  return ms > 0
    ? new Promise((resolve) => {
        setTimeout(resolve, ms);
      })
    : Promise.resolve();
}

/** The list is capped server-side (one Cognito lookup per row). */
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

interface IRunState {
  status: "idle" | "running" | "done";
  items: IRunItem[];
}

interface IConfirmRunModalProps {
  chosen: ICognitoMigrationPatient[];
  onConfirm: () => void;
}

/**
 * The one confirmation that matters on this page. Numbers first - how many
 * accounts get a new password, how many rows are skipped and why - then
 * three short points on what changes.
 */
function ConfirmRunModal({ chosen, onConfirm }: IConfirmRunModalProps) {
  const { closeModal } = useModal();

  const toMigrate = chosen.filter((patient) =>
    isMigratable(patient.cognitoStatus)
  ).length;
  const skipped = chosen.length - toMigrate;
  const count = (status: ICognitoMigrationPatient["cognitoStatus"]) =>
    chosen.filter((patient) => patient.cognitoStatus === status).length;
  const skipReasons = [
    { label: "already in Cognito", count: count("Migrated") },
    { label: "no email", count: count("NoEmail") },
    { label: "unexpected state", count: count("Unknown") },
  ].filter((reason) => reason.count > 0);

  return (
    <ModalContainer>
      <ModalTitle>Migrate to Cognito</ModalTitle>
      <ModalForm>
        <ConfirmStats>
          <ConfirmStatCard tone="primary">
            <ConfirmStatValue tone="primary">{toMigrate}</ConfirmStatValue>
            <ConfirmStatLabel>
              {toMigrate === 1 ? "patient" : "patients"} will be migrated
            </ConfirmStatLabel>
            <ConfirmStatHint>New random password each</ConfirmStatHint>
          </ConfirmStatCard>
          <ConfirmStatCard>
            <ConfirmStatValue>{skipped}</ConfirmStatValue>
            <ConfirmStatLabel>skipped</ConfirmStatLabel>
            <ConfirmStatHint>
              {skipReasons.length > 0
                ? skipReasons
                    .map((reason) => `${reason.count} ${reason.label}`)
                    .join(" · ")
                : "Nothing to skip"}
            </ConfirmStatHint>
          </ConfirmStatCard>
        </ConfirmStats>

        <ConfirmList>
          <li>Accounts already in Cognito are not changed.</li>
          <li>
            The old app keeps working with each patient&apos;s current password.
          </li>
          <li>
            The new passwords appear only in the results file. They are not
            stored anywhere.
          </li>
        </ConfirmList>

        <ModalFormActions>
          <FilterButton variant="secondary" onClick={closeModal}>
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={toMigrate === 0}
            onClick={() => {
              closeModal();
              onConfirm();
            }}
          >
            {toMigrate === 0
              ? "Nothing to migrate"
              : `Migrate ${toMigrate} patient${toMigrate === 1 ? "" : "s"}`}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}

function WarningTriangle() {
  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M12 9v4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <circle cx="12" cy="17" r="1.25" fill="currentColor" />
    </svg>
  );
}

interface IMigrationRunDialogProps {
  isRunning: boolean;
  items: IRunItem[];
  columns: ColumnDef<IRunItem, string>[];
  downloaded: boolean;
  /**
   * Owned by the page rather than the dialog so that a browser Back
   * navigation, which the page intercepts, can raise the same question.
   */
  confirmingClose: boolean;
  onConfirmingCloseChange: (confirming: boolean) => void;
  onCancelRun: () => void;
  onDownload: () => void;
  onClose: () => void;
}

/**
 * The run, in a dialog the operator cannot dismiss by accident. Built on the
 * same Radix primitives and styled parts as the shared Modal, but rendered
 * here rather than through the modal context: the shared one closes on an
 * outside click and freezes the node it is handed, and this content changes
 * with every processed row. Outside clicks and Escape do nothing; the close
 * control is disabled while the run is going and asks first while the
 * results still hold passwords nobody has downloaded.
 */
function MigrationRunDialog({
  isRunning,
  items,
  columns,
  downloaded,
  confirmingClose,
  onConfirmingCloseChange: setConfirmingClose,
  onCancelRun,
  onDownload,
  onClose,
}: IMigrationRunDialogProps) {
  const summary = summarize(items);
  const progressPct =
    summary.total === 0
      ? 0
      : Math.round((summary.processed / summary.total) * 100);
  const unsavedPasswords = !downloaded && hasUnsavedPasswords(items);

  // Closing always asks: even after a download the results cannot be reopened.
  const requestClose = () => {
    if (isRunning) return;
    setConfirmingClose(true);
  };

  return (
    <DialogPrimitive.Root open>
      <DialogPrimitive.Portal>
        <StyledDialogOverlay>
          <StyledDialogContent
            aria-describedby={undefined}
            onPointerDownOutside={(event) => event.preventDefault()}
            onInteractOutside={(event) => event.preventDefault()}
            onEscapeKeyDown={(event) => {
              event.preventDefault();
              requestClose();
            }}
          >
            <StyledTile css={{ padding: "24px" }}>
              <RunDialogBody>
                <SectionHeader>
                  <SectionTitleGroup>
                    <DialogPrimitive.Title asChild>
                      <SectionTitle>
                        {isRunning ? "Migrating..." : "Migration finished"}
                      </SectionTitle>
                    </DialogPrimitive.Title>
                    <SectionCaption>
                      {summary.processed} of {summary.total} processed
                    </SectionCaption>
                  </SectionTitleGroup>
                  <RunActions>
                    {isRunning ? (
                      <FilterButton variant="secondary" onClick={onCancelRun}>
                        Cancel after current
                      </FilterButton>
                    ) : (
                      <>
                        <FilterButton variant="primary" onClick={onDownload}>
                          Download results (.csv)
                        </FilterButton>
                        <FilterButton
                          variant="secondary"
                          aria-label="Close migration results"
                          onClick={requestClose}
                        >
                          Close
                        </FilterButton>
                      </>
                    )}
                  </RunActions>
                </SectionHeader>

                <ProgressTrack aria-label="Migration progress">
                  <ProgressFill css={{ width: `${progressPct}%` }} />
                </ProgressTrack>

                <StatsGrid>
                  <StatCard>
                    <StatLabel>Migrated</StatLabel>
                    <StatValue>{summary.migrated}</StatValue>
                  </StatCard>
                  <StatCard>
                    <StatLabel>Already in Cognito</StatLabel>
                    <StatValue>{summary.alreadyMigrated}</StatValue>
                  </StatCard>
                  <StatCard>
                    <StatLabel>Failed</StatLabel>
                    <StatValue>{summary.failed}</StatValue>
                  </StatCard>
                  <StatCard>
                    <StatLabel>Skipped</StatLabel>
                    <StatValue>{summary.skipped}</StatValue>
                  </StatCard>
                </StatsGrid>

                {summary.migrated > 0 && (
                  <WarningNotice>
                    The new passwords are in the results spreadsheet only. They
                    are not stored anywhere, so download the results before
                    closing this window
                    {downloaded ? "" : " - not downloaded yet"}.
                  </WarningNotice>
                )}

                <TableContainer>
                  <TableWrapper>
                    <TableSorted columns={columns} data={items} />
                  </TableWrapper>
                </TableContainer>
              </RunDialogBody>
            </StyledTile>
          </StyledDialogContent>
        </StyledDialogOverlay>
      </DialogPrimitive.Portal>

      {/* Stacked above the results (higher z-index than the shared overlay)
          and styled as a warning, so the question cannot be missed. */}
      <DialogPrimitive.Root open={confirmingClose}>
        <DialogPrimitive.Portal>
          <StyledDialogOverlay css={{ zIndex: 3100 }}>
            <StyledDialogContent
              aria-describedby={undefined}
              onPointerDownOutside={(event) => event.preventDefault()}
              onInteractOutside={(event) => event.preventDefault()}
              onEscapeKeyDown={(event) => {
                event.preventDefault();
                setConfirmingClose(false);
              }}
            >
              <StyledTile
                css={{
                  width: "min(680px, 92vw)",
                  padding: 0,
                  overflow: "hidden",
                  border: "1px solid $warning",
                  boxShadow: "0 12px 40px rgba(0, 0, 0, 0.35)",
                }}
              >
                <WarningDialogHeader>
                  <WarningDialogIcon aria-hidden="true">
                    <WarningTriangle />
                  </WarningDialogIcon>
                  <DialogPrimitive.Title asChild>
                    <WarningDialogTitle>
                      {unsavedPasswords
                        ? "Close without downloading the passwords?"
                        : "Close the results?"}
                    </WarningDialogTitle>
                  </DialogPrimitive.Title>
                </WarningDialogHeader>
                <WarningDialogBody>
                  {unsavedPasswords ? (
                    <ModalBodyText>
                      These results hold the <strong>only copy</strong> of the{" "}
                      {summary.migrated === 1
                        ? "password"
                        : `${summary.migrated} passwords`}{" "}
                      issued in this run. Once closed they{" "}
                      <strong>cannot be recovered</strong>, and the affected
                      patients would need a password reset.
                    </ModalBodyText>
                  ) : (
                    <ModalBodyText>
                      {downloaded
                        ? "The results file has been downloaded. "
                        : "No passwords were issued in this run. "}
                      Closing clears the results from this page; they cannot be
                      reopened.
                    </ModalBodyText>
                  )}
                  <WarningDialogActions>
                    <FilterButton
                      variant="secondary"
                      onClick={() => setConfirmingClose(false)}
                    >
                      Keep results
                    </FilterButton>
                    <WarningDialogActionsGroup>
                      {unsavedPasswords ? (
                        <>
                          <FilterButton variant="secondary" onClick={onClose}>
                            Close without downloading
                          </FilterButton>
                          <FilterButton
                            variant="primary"
                            onClick={() => {
                              onDownload();
                              onClose();
                            }}
                          >
                            Download and close
                          </FilterButton>
                        </>
                      ) : (
                        <FilterButton variant="primary" onClick={onClose}>
                          Close results
                        </FilterButton>
                      )}
                    </WarningDialogActionsGroup>
                  </WarningDialogActions>
                </WarningDialogBody>
              </StyledTile>
            </StyledDialogContent>
          </StyledDialogOverlay>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </DialogPrimitive.Root>
  );
}

/**
 * Cognito Migration admin page (EN-5222).
 *
 * Legacy-app patients are migrated to Cognito on their first login to the new
 * app, using the password they already have. When that password fails the
 * Cognito policy the login now sends a set-password email instead — but a
 * site whose patients use dummy mailboxes (EurILD) cannot act on those
 * emails. This page is the alternative: pick a hospital (and ward), select
 * patients, and migrate them with a random policy-compliant password each,
 * then hand the site the spreadsheet of new passwords.
 *
 * The run is driven from here, one request per patient in sequence, so the
 * table can show each row being processed and a failure on one patient never
 * stops the rest. The API never stores the passwords; this page keeps them in
 * memory until the results file is downloaded, and warns before they are lost.
 */
export function AdminCognitoMigration() {
  document.title = "Cognito migration - Admin";

  const queryClient = useQueryClient();
  const { openModal } = useModal();

  const [hospitalId, setHospitalId] = useState("");
  const [wardId, setWardId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  // Keyed by patient id and holding the row, so a selection can span pages
  // and the run does not depend on the page still being on screen.
  const [selected, setSelected] = useState<
    Map<number, ICognitoMigrationPatient>
  >(new Map());
  const [run, setRun] = useState<IRunState>({ status: "idle", items: [] });
  const [downloaded, setDownloaded] = useState(false);
  const [confirmingClose, setConfirmingClose] = useState(false);
  const cancelRef = useRef(false);

  const debouncedSearch = useDebounce(searchTerm, 400);
  const hospitalGroupId = hospitalId ? Number(hospitalId) : undefined;
  const wardFilter = wardId ? Number(wardId) : undefined;

  const { hospitals } = useHospitals();
  const { wards } = useHospitalWards(hospitalGroupId);
  const { patients, pagination, isLoading, isFetching, isError } =
    useCognitoMigrationPatients(
      hospitalGroupId,
      wardFilter,
      debouncedSearch,
      page,
      pageSize
    );
  const { migratePatient } = useMigratePatientToCognito();

  const hospitalName = hospitals.find(
    (hospital) => String(hospital.id) === hospitalId
  )?.name;

  const isRunning = run.status === "running";
  const unsavedPasswords = !downloaded && hasUnsavedPasswords(run.items);

  // Leaving the page loses the only copy of the passwords. The browser's own
  // prompt is the one thing that can intercept a tab close.
  useEffect(() => {
    if (!unsavedPasswords && !isRunning) return undefined;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // The legacy return value is what older browsers key the prompt on.
      // eslint-disable-next-line no-param-reassign
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [unsavedPasswords, isRunning]);

  // The browser's Back button is a same-document navigation: no beforeunload,
  // and the page (with every issued password) simply unmounts. The router is
  // built with useRoutes, so there is no blocker API; instead push a duplicate
  // history entry while the run holds state and, when Back pops it, push it
  // again and ask the same question the Close control asks.
  useEffect(() => {
    if (!unsavedPasswords && !isRunning) return undefined;
    window.history.pushState(null, "", window.location.href);
    const onPopState = () => {
      window.history.pushState(null, "", window.location.href);
      if (!isRunning) setConfirmingClose(true);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [unsavedPasswords, isRunning]);

  const resetListState = () => {
    setPage(1);
    setSelected(new Map());
  };

  const handleHospitalChange = (value: string) => {
    setHospitalId(value);
    setWardId("");
    resetListState();
  };

  const handleWardChange = (value: string) => {
    setWardId(value);
    resetListState();
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const toggleOne = useCallback((patient: ICognitoMigrationPatient) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(patient.patientId)) next.delete(patient.patientId);
      else next.set(patient.patientId, patient);
      return next;
    });
  }, []);

  const pageAllSelected =
    patients.length > 0 &&
    patients.every((patient) => selected.has(patient.patientId));
  const pageSomeSelected = patients.some((patient) =>
    selected.has(patient.patientId)
  );

  const togglePage = () => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (pageAllSelected) {
        patients.forEach((patient) => next.delete(patient.patientId));
      } else {
        patients.forEach((patient) => next.set(patient.patientId, patient));
      }
      return next;
    });
  };

  const updateItem = (index: number, patch: Partial<IRunItem>) => {
    setRun((prev) => {
      const items = prev.items.slice();
      items[index] = { ...items[index], ...patch };
      return { ...prev, items };
    });
  };

  // A 429 means the API's rule fired anyway; wait it out once instead of
  // burning the row.
  const migrateWithRetry = async (patientId: number) => {
    try {
      return await migratePatient(patientId);
    } catch (error) {
      if (!(error instanceof HttpError) || error.status !== 429) throw error;
      await sleep(RATE_LIMIT_RETRY_MS);
      return migratePatient(patientId);
    }
  };

  const runMigration = async (items: IRunItem[]) => {
    cancelRef.current = false;
    setDownloaded(false);
    setRun({ status: "running", items });

    // Sequential on purpose: one patient at a time is what lets the table show
    // each row being processed, keeps Cognito's admin quota shared with logins
    // untouched, and is what makes "Cancel after current" mean something.
    for (let index = 0; index < items.length; index += 1) {
      const item = items[index];
      // Rows decided at planning time (already in Cognito, no email) are
      // already "done" and never sent.
      if (item.state !== "done") {
        if (cancelRef.current) {
          updateItem(index, { state: "done", skipped: "cancelled" });
        } else {
          updateItem(index, { state: "running" });
          const startedAt = Date.now();
          try {
            // eslint-disable-next-line no-await-in-loop
            const result = await migrateWithRetry(item.patient.patientId);
            updateItem(index, { state: "done", result });
          } catch (error) {
            updateItem(index, {
              state: "done",
              requestError: errorToastMessage(error, "The request failed."),
            });
          }
          // The API's rate-limit rule for this endpoint assumes about one
          // patient per second; make that true rather than rely on latency.
          // eslint-disable-next-line no-await-in-loop
          await sleep(MIN_REQUEST_INTERVAL_MS - (Date.now() - startedAt));
        }
      }
    }

    setRun((prev) => ({ ...prev, status: "done" }));
    setSelected(new Map());
    // Statuses in the list changed under it; one refetch at the end rather
    // than one per row, which would race the run's own Cognito calls.
    queryClient.invalidateQueries({
      queryKey: [COGNITO_MIGRATION_PATIENTS_QUERY_KEY],
    });
  };

  const handleMigrateClick = () => {
    const chosen = Array.from(selected.values());
    openModal(
      <ConfirmRunModal
        chosen={chosen}
        onConfirm={() => runMigration(planRun(chosen))}
      />
    );
  };

  const handleDownload = () => {
    const csv = buildResultsCsv(run.items);
    saveBlobAs(
      new Blob([csv], { type: "text/csv;charset=utf-8;" }),
      resultsFilename(hospitalName)
    );
    setDownloaded(true);
  };

  const closeRun = () => {
    setConfirmingClose(false);
    setRun({ status: "idle", items: [] });
    setDownloaded(false);
  };

  const columns: ColumnDef<ICognitoMigrationPatient, string>[] = useMemo(
    () => [
      {
        id: "select",
        // No accessor, so the table would print its "-" placeholder instead
        // of the checkbox without this opt-out.
        meta: { renderEmpty: true },
        header: () => (
          <SelectAllCheckbox
            checked={pageAllSelected}
            indeterminate={!pageAllSelected && pageSomeSelected}
            disabled={isRunning || patients.length === 0}
            onChange={togglePage}
          />
        ),
        cell: ({ row }) => (
          <SelectCell
            type="checkbox"
            aria-label={`Select ${row.original.name}`}
            checked={selected.has(row.original.patientId)}
            disabled={isRunning}
            onClick={(event) => event.stopPropagation()}
            onChange={() => toggleOne(row.original)}
          />
        ),
        enableSorting: false,
      },
      {
        id: "patientId",
        header: "ID",
        accessorFn: (patient) => String(patient.patientId),
        enableSorting: true,
      },
      { accessorKey: "name", header: "Name", enableSorting: true },
      {
        accessorKey: "email",
        header: "Email",
        cell: ({ row }) =>
          row.original.email ? (
            <EmailLink
              href={`mailto:${row.original.email}`}
              onClick={(event) => event.stopPropagation()}
            >
              {row.original.email}
            </EmailLink>
          ) : (
            <span>-</span>
          ),
        enableSorting: true,
      },
      {
        id: "mrn",
        header: "MRN",
        accessorFn: (patient) => patient.mrn ?? "-",
        enableSorting: true,
      },
      {
        id: "ward",
        header: "Ward",
        accessorFn: (patient) => patient.ward ?? "-",
        enableSorting: true,
      },
      {
        id: "legacyPasswordSet",
        header: "Old app password",
        accessorFn: (patient) => patient.legacyPasswordSet || "-",
        cell: ({ row }) => (
          <StatusBadge status={passwordSetTone(row.original.legacyPasswordSet)}>
            {row.original.legacyPasswordSet || "-"}
          </StatusBadge>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "lastUsed",
        header: "Last used",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.lastUsed, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        id: "cognitoStatus",
        header: "Cognito",
        accessorFn: (patient) => STATUS_LABEL[patient.cognitoStatus],
        cell: ({ row }) => (
          <StatusBadge status={statusTone(row.original.cognitoStatus)}>
            {STATUS_LABEL[row.original.cognitoStatus]}
          </StatusBadge>
        ),
        enableSorting: true,
      },
    ],
    [
      patients,
      selected,
      pageAllSelected,
      pageSomeSelected,
      isRunning,
      toggleOne,
    ]
  );

  const runColumns: ColumnDef<IRunItem, string>[] = useMemo(
    () => [
      {
        id: "patientId",
        header: "ID",
        accessorFn: (item) => String(item.patient.patientId),
        enableSorting: false,
      },
      {
        id: "name",
        header: "Name",
        accessorFn: (item) => item.patient.name,
        enableSorting: false,
      },
      {
        id: "email",
        header: "Email",
        accessorFn: (item) => item.patient.email ?? "-",
        enableSorting: false,
      },
      {
        id: "result",
        header: "Result",
        accessorFn: (item) => itemResultLabel(item),
        cell: ({ row }) => (
          <StatusBadge status={itemTone(row.original)}>
            {itemResultLabel(row.original)}
          </StatusBadge>
        ),
        enableSorting: false,
      },
      {
        id: "detail",
        header: "Details",
        accessorFn: (item) => itemDetail(item),
        cell: ({ row }) => (
          <DetailCell>{itemDetail(row.original) || "-"}</DetailCell>
        ),
        enableSorting: false,
      },
    ],
    []
  );

  const loadingLabel = "Checking Cognito status for this page…";

  let emptyStateText: React.ReactNode = "No active patients found";
  if (!hospitalGroupId) {
    emptyStateText = "Select a hospital to list its patients";
  } else if (isLoading || isFetching) {
    emptyStateText = <TableLoadingState label={loadingLabel} />;
  }

  // A page change keeps the previous rows on screen (keepPreviousData); the
  // overlay is what says the next page is on its way.
  const showFetchingOverlay =
    hospitalGroupId !== undefined && isFetching && patients.length > 0;

  const selectedCount = selected.size;
  const selectedMigratable = Array.from(selected.values()).filter((patient) =>
    isMigratable(patient.cognitoStatus)
  ).length;

  return (
    <PageContainer>
      <PageHeader>
        <PageTitleGroup>
          <PageTitle>Cognito migration</PageTitle>
          <PageInfoTooltip title="Cognito migration">
            Migrate a hospital&apos;s old-app patients to Cognito with a new
            random password each, for sites whose patients cannot use the
            set-password email. Accounts already in Cognito are left alone. The
            old app keeps working with the patient&apos;s current password.
          </PageInfoTooltip>
        </PageTitleGroup>
      </PageHeader>

      <FiltersContainer>
        <FilterField>
          <FilterLabel htmlFor="cognito-migration-hospital">
            Hospital
          </FilterLabel>
          <AdminSelectField
            id="cognito-migration-hospital"
            value={hospitalId}
            disabled={isRunning}
            onChange={(event) => handleHospitalChange(event.target.value)}
          >
            <option value="">Select a hospital</option>
            {hospitals.map((hospital) => (
              <option key={hospital.id} value={String(hospital.id)}>
                {hospital.name}
              </option>
            ))}
          </AdminSelectField>
        </FilterField>
        <FilterField>
          <FilterLabel htmlFor="cognito-migration-ward">Ward</FilterLabel>
          <AdminSelectField
            id="cognito-migration-ward"
            value={wardId}
            disabled={isRunning || !hospitalGroupId}
            onChange={(event) => handleWardChange(event.target.value)}
          >
            <option value="">All wards</option>
            {wards.map((ward) => (
              <option key={ward.id} value={String(ward.id)}>
                {ward.name}
              </option>
            ))}
          </AdminSelectField>
        </FilterField>
        <SearchInput
          type="text"
          placeholder="Search by name, email or MRN..."
          value={searchTerm}
          disabled={!hospitalGroupId}
          onChange={(event) => handleSearchChange(event.target.value)}
        />
        <FiltersActions>
          <SelectionCount>
            {selectedCount === 0
              ? "No patients selected"
              : `${selectedCount} selected, ${selectedMigratable} to migrate`}
          </SelectionCount>
          <FilterButton
            variant="primary"
            disabled={isRunning || selectedCount === 0}
            onClick={handleMigrateClick}
          >
            Migrate to Cognito
          </FilterButton>
        </FiltersActions>
      </FiltersContainer>

      {run.status !== "idle" && (
        <MigrationRunDialog
          isRunning={isRunning}
          items={run.items}
          columns={runColumns}
          downloaded={downloaded}
          confirmingClose={confirmingClose}
          onConfirmingCloseChange={setConfirmingClose}
          onCancelRun={() => {
            cancelRef.current = true;
          }}
          onDownload={handleDownload}
          onClose={closeRun}
        />
      )}

      {isError && (
        <ErrorMessage>
          Failed to load patients. Try refreshing the page.
        </ErrorMessage>
      )}

      <TableContainer>
        <TableStage aria-busy={showFetchingOverlay}>
          <TableWrapper refreshKeys={[[COGNITO_MIGRATION_PATIENTS_QUERY_KEY]]}>
            <TableSorted
              columns={columns}
              data={patients}
              onRowClick={(patient: ICognitoMigrationPatient) => {
                if (!isRunning) toggleOne(patient);
              }}
              emptyStateText={emptyStateText}
            />
          </TableWrapper>
          {showFetchingOverlay && (
            <TableFetchingOverlay role="status">
              <TableLoadingState label={loadingLabel} />
            </TableFetchingOverlay>
          )}
        </TableStage>
        {hospitalGroupId !== undefined && (
          <TablePagination
            pagination={pagination}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        )}
      </TableContainer>
    </PageContainer>
  );
}
